/* eslint-disable react/forbid-prop-types */
import {
  Box,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TableRow,
  LinearProgress,
  Paper,
  TableHead,
  Card,
  CardContent,
  Typography,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import {
  React,
  useEffect,
  useState,
} from 'react';
import moment from 'moment';
import BrandObjectListItem from '../../../components/MachineTelemetry/BrandObjectListItem';
import SecondaryButton from '../../../components/utils/SecondaryButton';
import useCSVExport from '../../../hooks/useCSVExport';
import useGuardedExport from '../../../hooks/useGuardedExport';
import { StyledTableCell } from '../../../components/utils/StyledTable';
import * as IoTFotaExitCodes from '../IoTFotaExitCodes.json';
import * as IoTFotaStatusCodes from '../IoTFotaStatusCodes.json';
import useAnalytics from '../../../hooks/useAnalytics';

const useStyles = makeStyles((theme) => ({
  headerTable: {
    font: theme.typography.h5.font,
    fontSize: 14,
    color: theme.palette.common.white,
  },
  headerBorderTable: {
    borderBottom: `1.5px solid ${theme.palette.primary.banner}`,
  },
  bottomRightExportButton: {
    paddingRight: 8,
    paddingLeft: 8,
    paddingBottom: 16,
    textAlign: 'right',
    alignSelf: 'flex-end',
    borderRadius: 0,
    width: 138,
  },
  historyTable: {
    borderRadius: 0,
    minWidth: 200,
    tableLayout: 'fixed',
    width: '100%',
    '& tr': {
      '& td': {
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 12,
        paddingRight: 8,
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        verticalAlign: 'top',
        cursor: 'default',
        '&.link-cell': {
          cursor: 'pointer',
          color: theme.palette.primary.main,
          textDecoration: 'none !important',
          '&:hover': {
            cursor: 'pointer',
            color: theme.palette.primary.dark,
          },
          '& a': {
            color: 'inherit',
            textDecoration: 'none',
          },
        },
      },
    },
    '& th': {
      paddingLeft: 12,
      paddingRight: 8,
      verticalAlign: 'top',
      cursor: 'default',
    },
  },
  deployIdColumn: {
    width: '160px !important',
    maxWidth: '160px !important',
  },
  typeColumn: {
    width: '60px !important',
    maxWidth: '60px !important',
  },
  statusDateColumn: {
    width: '80px !important',
    maxWidth: '80px !important',
  },
  firmwareColumn: {
    width: '50px !important',
    maxWidth: '50px !important',
  },
  hardwareColumn: {
    width: '50px !important',
    maxWidth: '50px !important',
  },
  exitCodeColumn: {
    width: '110px !important',
    maxWidth: '110px !important',
  },
  statusColumn: {
    width: '110px !important',
    maxWidth: '110px !important',
  },
  attemptsColumn: {
    width: '30px !important',
    maxWidth: '30px !important',
  },
  separate: {
    borderRadius: 12,
  },
  separateVoidGrid: {
    flexWrap: 'noWrap',
    borderRadius: '12px !important',
  },
  content: {
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 24,
    paddingRight: 24,
    backgroundColor: theme.palette.background.default,
  },
  contentGrid: {
    paddingBottom: 16,
  },
  cardNoMachineTelemetryText: {
    color: theme.palette.primary.text,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: 16,
    textAlign: 'center',
  },
  detailsTitle: {
    fontSize: 14,
    fontWeight: theme.typography.fontWeightMedium,
    paddingRight: '5px',
  },
  headingTitle: {
    fontSize: 16,
    fontWeight: theme.typography.fontWeightMedium,
  },
  machineInfoCardPadding16: {
    padding: '16px !important',
  },
  telemetryTable: {
    borderRadius: 12,
    boxShadow: theme.customShadows.noTopShadow,
  },
  bottomLeftLegend: {
    paddingTop: 4,
  },
  cardNoMachineTelemetry: {
    borderRadius: 12,
  },
  footerGrid: {
    width: 9,
  },
}));

const findExitCodeDescription = (code) => {
  const exitCodeList = IoTFotaExitCodes.default || IoTFotaExitCodes;
  const found = exitCodeList.find((item) => item.code === code);
  return found ? `${code} (${found.message})` : code;
};

const findStatusCodeDescription = (status) => {
  const statusCodeList = IoTFotaStatusCodes.default || IoTFotaStatusCodes;
  const found = statusCodeList.find((item) => item.id === status || item.code === status);
  return found ? `${status} (${found.name})` : status;
};

const getColumnConfig = (project) => [
  { key: 'statusdate', displayName: 'Updated at (UTC)', format: (value) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '') },
  {
    key: 'deployid',
    displayName: 'Deploy ID',
    isLink: true,
    format: (value) => value,
    linkFormat: (value) => `#/deployments/${project.code}/${value}`
  },
  { key: 'deploytype', displayName: 'Type' },
  { key: 'fw', displayName: 'Previous Firmware Version' },
  { key: 'hw', displayName: 'Hardware Version' },
  { key: 'exitcode', displayName: 'Exit Code', format: (value) => findExitCodeDescription(value) },
  { key: 'status', displayName: 'Status', format: (value) => findStatusCodeDescription(value) },
  { key: 'state_count', displayName: 'Number of attempts' },
];

export default function DeployEventsHistoryTablePaginated({
  dataList, filters, loading, dateIni, dateEnd,
  object, params, id, project
}) {
  const [rawData, setRawData] = useState([]);
  const MAX_DAYS_BETWEEN_DATES = 90;

  const classes = useStyles();
  
  const columnConfig = getColumnConfig(project);
  const headerExport = columnConfig.map((col) => col.displayName);

  useEffect(() => {
    setRawData(dataList || []);
  }, [dataList]);

  const processedData = rawData.map((item) => {
    const newItem = {};
    columnConfig.forEach((col) => {
      const value = item[col.key];
      const formattedValue = col.format ? col.format(value) : value;

      if (col.isLink && col.linkFormat && value) {
        // Create a special object for link columns
        newItem[col.displayName] = {
          value: formattedValue,
          isLink: true,
          href: col.linkFormat(value),
          target: '_blank',
          rel: 'noopener noreferrer'
        };
      } else {
        newItem[col.displayName] = formattedValue;
      }
    });
    return newItem;
  });

  // Create a map of which columns are links
  const linkColumnsMap = {};
  columnConfig.forEach((col) => {
    linkColumnsMap[col.displayName] = col.isLink || false;
  });

  const {
    exportToCSV, loading: exportLoading,
  } = useCSVExport(
    params, filters, dateIni, dateEnd, MAX_DAYS_BETWEEN_DATES, object,
  );

  const { setAnalytics } = useAnalytics();

  const { requestExport, modal: exportModal } = useGuardedExport(
    {
      action: /machinelist|machineassetsdetail|machinecount/i.test(object) ? 'lifeCycleObject' : 'telemetryObject',
      projectId: project?.code,
      object,
      dateIni,
      dateEnd,
      queryParams: typeof params === 'string' ? params : JSON.stringify(params || {}),
      filters: typeof filters === 'string' ? filters : JSON.stringify(filters || {}),
      title: 'Export deploy events to CSV',
    },
    exportToCSV,
  );

  const handleClickExport = (e) => {
    e.preventDefault();
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: 'device_management-overview-info-bookmark',
      }

    });
    console.log('[Analytics] button_clicked: device_management-overview-info-bookmark');

    requestExport();
  };

  return (
    <Box>
      <Grid
        container
        spacing={0}
        direction="row"
        className={classes.content}
        id="brand-events-table"
      >
        {(rawData !== null) && (Array.isArray(rawData)) && (rawData.length > 0)
          && (
            <Grid
              container
              spacing={0}
              direction="row"
              className={classes.contentGrid}
            >
              <Grid item xs={6} className={classes.bottomLeftLegend}>
                <Typography className={classes.bottomLeftLegend}>Data will be retrieved in real time</Typography>
              </Grid>

              <Grid item xs={6} className={classes.bottomRightExportButton}>
                <span>
                  <SecondaryButton
                    id={`btn${id}Export`}
                    onClick={handleClickExport}
                    disabled={exportLoading}
                  >
                    Export .CSV
                  </SecondaryButton>
                </span>
                {exportModal}
              </Grid>

              <Grid
                item
                xs={12}
                className={classes.separate}
              >
                <TableContainer component={Paper} className={classes.telemetryTable}>
                  <Table className={classes.historyTable} aria-label="custom pagination table">
                    <TableHead>
                      <TableRow>
                        {headerExport.map((head, index) => {
                          const columnClasses = [
                            classes.statusDateColumn,
                            classes.deployIdColumn,
                            classes.typeColumn,
                            classes.firmwareColumn,
                            classes.hardwareColumn,
                            classes.exitCodeColumn,
                            classes.statusColumn,
                            classes.attemptsColumn,
                          ];
                          return (
                            <StyledTableCell
                              className={`${classes.headerTable} ${columnClasses[index]}`}
                              key={`header-${head}-${index}`}
                              id={`header${id}-${index}`}
                            >
                              {head}
                            </StyledTableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {processedData.map((item, i) => (
                        <BrandObjectListItem
                          key={`row-${i}`}
                          object={item}
                          id={`cell${id}${i}`}
                          linkColumnsMap={linkColumnsMap}
                        />
                      ))}
                    </TableBody>
                    <Grid className={classes.footerGrid}>
                      <TableFooter />
                    </Grid>
                  </Table>
                  {loading && <LinearProgress />}
                </TableContainer>
              </Grid>
            </Grid>
          )}
        {(rawData !== null) && (Array.isArray(rawData)) && (rawData.length === 0)
          && !loading
          && (
            <Grid
              item
              xs={12}
              className={classes.separateVoidGrid}
            >
              <Card className={classes.cardNoMachineTelemetry}>
                <CardContent className={classes.machineInfoCardPadding16}>
                  <Typography className={classes.cardNoMachineTelemetryText}>
                    No results found
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
      </Grid>
    </Box>
  );
}

DeployEventsHistoryTablePaginated.propTypes = {
  dataList: PropTypes.array,
  filters: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  dateIni: PropTypes.object.isRequired,
  dateEnd: PropTypes.object,
  object: PropTypes.string.isRequired,
  params: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  columnInclude: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  project: PropTypes.object.isRequired,
};

DeployEventsHistoryTablePaginated.defaultProps = {
  dataList: [],
  columnInclude: '',
  dateEnd: null,
  filters: JSON.stringify({}),
};
